import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';

const GLITCH_CHARS = '!@#$%^&*<>?/\\|{}[]0123456789ABCDEF';

const useGlitchText = (text: string, active: boolean) => {
  const [display, setDisplay] = useState(text);
  const frameRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const iterRef = useRef(0);

  useEffect(() => {
    if (!active) { setDisplay(text); return; }
    iterRef.current = 0;
    frameRef.current = setInterval(() => {
      iterRef.current += 1;
      setDisplay(
        text
          .split('')
          .map((char, idx) => {
            if (char === ' ') return ' ';
            if (idx < iterRef.current) return char;
            return GLITCH_CHARS[Math.floor(Math.random() * GLITCH_CHARS.length)];
          })
          .join('')
      );
      if (iterRef.current >= text.length) {
        if (frameRef.current) clearInterval(frameRef.current);
        setDisplay(text);
      }
    }, 40);
    return () => { if (frameRef.current) clearInterval(frameRef.current); };
  }, [text, active]);

  return display;
};

const QUICK_LINKS = [
  { label: 'Главная', to: '/', icon: 'ri-home-line', color: '#ff2d78' },
  { label: 'Каталог', to: '/catalog', icon: 'ri-medicine-bottle-line', color: '#00f5ff' },
  { label: 'Акции', to: '/promos', icon: 'ri-gift-line', color: '#39ff14' },
  { label: 'Поиск', to: '/search', icon: 'ri-search-line', color: '#f0ff00' },
  { label: 'Доставка', to: '/delivery', icon: 'ri-truck-line', color: '#ff6b00' },
  { label: 'Контакты', to: '/contacts', icon: 'ri-phone-line', color: '#ff2d78' },
];

export default function NotFound() {
  const navigate = useNavigate();
  const location = useLocation();
  const [glitchActive, setGlitchActive] = useState(false);
  const [countdown, setCountdown] = useState(15);
  const [autoRedirect, setAutoRedirect] = useState(true);
  const canvasRef = useRef<HTMLCanvasElement>(null);

  const glitchTitle = useGlitchText('СТРАНИЦА НЕ НАЙДЕНА', glitchActive);

  /* Trigger glitch on mount and periodically */
  useEffect(() => {
    setGlitchActive(true);
    const timer = setTimeout(() => setGlitchActive(false), 1200);
    const interval = setInterval(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 900);
    }, 5000);
    return () => { clearTimeout(timer); clearInterval(interval); };
  }, []);

  /* Countdown auto-redirect */
  useEffect(() => {
    if (!autoRedirect) return;
    if (countdown <= 0) { navigate('/'); return; }
    const t = setTimeout(() => setCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [countdown, autoRedirect, navigate]);

  /* Matrix rain canvas */
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener('resize', resize);

    const cols = Math.floor(canvas.width / 20);
    const drops: number[] = Array(cols).fill(1);
    const chars = '01アイウエオカキクケコサシスセソタチツテトナニヌネノ';

    const draw = () => {
      ctx.fillStyle = 'rgba(5,5,8,0.06)';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.fillStyle = 'rgba(255,45,120,0.18)';
      ctx.font = '13px monospace';
      drops.forEach((y, i) => {
        const char = chars[Math.floor(Math.random() * chars.length)];
        ctx.fillText(char, i * 20, y * 20);
        if (y * 20 > canvas.height && Math.random() > 0.975) drops[i] = 0;
        drops[i]++;
      });
    };

    const id = setInterval(draw, 55);
    return () => { clearInterval(id); window.removeEventListener('resize', resize); };
  }, []);

  const progressPct = ((15 - countdown) / 15) * 100;

  return (
    <div
      className="relative min-h-screen w-full flex flex-col items-center justify-center overflow-hidden"
      style={{ background: '#050508' }}
    >
      {/* Matrix rain background */}
      <canvas
        ref={canvasRef}
        className="absolute inset-0 w-full h-full pointer-events-none"
        style={{ opacity: 0.6 }}
      />

      {/* Grid overlay */}
      <div className="absolute inset-0 grid-bg opacity-20 pointer-events-none" />

      {/* Ambient glows */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full pointer-events-none" style={{ background: '#ff2d78', filter: 'blur(120px)', opacity: 0.07 }} />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full pointer-events-none" style={{ background: '#00f5ff', filter: 'blur(100px)', opacity: 0.06 }} />

      {/* Scanlines */}
      <div className="absolute inset-0 scanline opacity-20 pointer-events-none" />

      {/* Corner accents */}
      {[
        'top-0 left-0',
        'top-0 right-0',
        'bottom-0 left-0',
        'bottom-0 right-0',
      ].map((pos, i) => (
        <div key={i} className={`absolute ${pos} w-16 h-16 pointer-events-none`}>
          <div
            className="absolute inset-0"
            style={{
              borderTop: i < 2 ? '1px solid rgba(255,45,120,0.4)' : 'none',
              borderBottom: i >= 2 ? '1px solid rgba(255,45,120,0.4)' : 'none',
              borderLeft: i % 2 === 0 ? '1px solid rgba(255,45,120,0.4)' : 'none',
              borderRight: i % 2 === 1 ? '1px solid rgba(255,45,120,0.4)' : 'none',
            }}
          />
        </div>
      ))}

      {/* Main content */}
      <div className="relative z-10 flex flex-col items-center text-center px-6 max-w-3xl w-full">

        {/* 404 big number */}
        <div className="relative mb-4 select-none">
          {/* Shadow layers for depth */}
          <div
            className="absolute inset-0 font-orbitron font-black leading-none"
            style={{
              fontSize: 'clamp(120px, 20vw, 220px)',
              color: 'transparent',
              WebkitTextStroke: '1px rgba(255,45,120,0.15)',
              transform: 'translate(4px, 4px)',
            }}
          >
            404
          </div>
          <div
            className="absolute inset-0 font-orbitron font-black leading-none"
            style={{
              fontSize: 'clamp(120px, 20vw, 220px)',
              color: 'transparent',
              WebkitTextStroke: '1px rgba(0,245,255,0.12)',
              transform: 'translate(-3px, -3px)',
            }}
          >
            404
          </div>
          <h1
            className="font-orbitron font-black leading-none relative"
            style={{
              fontSize: 'clamp(120px, 20vw, 220px)',
              color: 'transparent',
              WebkitTextStroke: '2px #ff2d78',
              textShadow: '0 0 40px rgba(255,45,120,0.5), 0 0 80px rgba(255,45,120,0.2)',
            }}
          >
            404
          </h1>
        </div>

        {/* Glitch title */}
        <div className="mb-3 relative">
          <p
            className="font-orbitron font-black text-xl md:text-3xl tracking-[0.15em]"
            style={{
              color: glitchActive ? '#00f5ff' : 'rgba(255,255,255,0.85)',
              textShadow: glitchActive ? '0 0 20px #00f5ff, 2px 0 #ff2d78, -2px 0 #39ff14' : 'none',
              transition: 'color 0.1s, text-shadow 0.1s',
            }}
          >
            {glitchTitle}
          </p>
          {/* Glitch offset layers */}
          {glitchActive && (
            <>
              <p
                className="absolute inset-0 font-orbitron font-black text-xl md:text-3xl tracking-[0.15em] pointer-events-none"
                style={{ color: '#ff2d78', opacity: 0.5, transform: 'translate(3px, 0)', clipPath: 'inset(30% 0 40% 0)' }}
              >
                {glitchTitle}
              </p>
              <p
                className="absolute inset-0 font-orbitron font-black text-xl md:text-3xl tracking-[0.15em] pointer-events-none"
                style={{ color: '#39ff14', opacity: 0.4, transform: 'translate(-3px, 0)', clipPath: 'inset(60% 0 10% 0)' }}
              >
                {glitchTitle}
              </p>
            </>
          )}
        </div>

        {/* Path display */}
        <div
          className="flex items-center gap-2 px-4 py-2 rounded-lg mb-6 font-mono text-sm"
          style={{ background: 'rgba(255,45,120,0.06)', border: '1px solid rgba(255,45,120,0.2)' }}
        >
          <i className="ri-error-warning-line text-base" style={{ color: '#ff2d78' }} />
          <span className="text-white/40">Запрошенный путь:</span>
          <span style={{ color: '#ff2d78' }}>{location.pathname}</span>
        </div>

        <p className="font-mono text-sm text-white/40 mb-10 max-w-md leading-relaxed">
          Эта страница была удалена, перемещена или никогда не существовала в нашей системе. Проверьте адрес или воспользуйтесь навигацией.
        </p>

        {/* Action buttons */}
        <div className="flex flex-col sm:flex-row gap-3 mb-10 w-full max-w-sm">
          <button
            onClick={() => navigate(-1)}
            className="whitespace-nowrap flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-orbitron font-bold text-sm tracking-wider cursor-pointer transition-all duration-300"
            style={{ border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.6)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.35)'; (e.currentTarget as HTMLElement).style.color = '#fff'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = 'rgba(255,255,255,0.15)'; (e.currentTarget as HTMLElement).style.color = 'rgba(255,255,255,0.6)'; }}
          >
            <i className="ri-arrow-left-line" />
            НАЗАД
          </button>
          <Link
            to="/"
            className="whitespace-nowrap flex-1 flex items-center justify-center gap-2 py-3.5 rounded-xl font-orbitron font-bold text-sm tracking-wider cursor-pointer transition-all duration-300"
            style={{ background: '#ff2d78', color: '#fff', boxShadow: '0 0 20px rgba(255,45,120,0.4)' }}
            onMouseEnter={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 35px rgba(255,45,120,0.7)'; }}
            onMouseLeave={e => { (e.currentTarget as HTMLElement).style.boxShadow = '0 0 20px rgba(255,45,120,0.4)'; }}
          >
            <i className="ri-home-line" />
            НА ГЛАВНУЮ
          </Link>
        </div>

        {/* Auto-redirect countdown */}
        {autoRedirect && (
          <div
            className="w-full max-w-sm mb-10 p-4 rounded-xl"
            style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.07)' }}
          >
            <div className="flex items-center justify-between mb-2.5">
              <span className="font-mono text-xs text-white/30 flex items-center gap-1.5">
                <i className="ri-time-line" style={{ color: '#00f5ff' }} />
                Автоматический переход на главную через
              </span>
              <div className="flex items-center gap-2">
                <span className="font-orbitron font-black text-lg" style={{ color: '#00f5ff', textShadow: '0 0 10px #00f5ff' }}>
                  {countdown}с
                </span>
                <button
                  onClick={() => setAutoRedirect(false)}
                  className="w-5 h-5 flex items-center justify-center rounded-full cursor-pointer transition-colors"
                  style={{ background: 'rgba(255,255,255,0.06)' }}
                  onMouseEnter={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,45,120,0.2)'; }}
                  onMouseLeave={e => { (e.currentTarget as HTMLElement).style.background = 'rgba(255,255,255,0.06)'; }}
                >
                  <i className="ri-close-line text-xs text-white/40" />
                </button>
              </div>
            </div>
            {/* Progress bar */}
            <div className="h-1 rounded-full overflow-hidden" style={{ background: 'rgba(255,255,255,0.06)' }}>
              <div
                className="h-full rounded-full transition-all duration-1000"
                style={{
                  width: `${progressPct}%`,
                  background: 'linear-gradient(90deg, #ff2d78, #00f5ff)',
                  boxShadow: '0 0 8px rgba(0,245,255,0.6)',
                }}
              />
            </div>
          </div>
        )}

        {/* Quick links */}
        <div className="w-full max-w-lg">
          <p className="font-mono text-[10px] tracking-[0.3em] text-white/20 mb-4">БЫСТРАЯ НАВИГАЦИЯ</p>
          <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
            {QUICK_LINKS.map(link => (
              <Link
                key={link.to}
                to={link.to}
                className="flex flex-col items-center gap-2 p-3 rounded-xl cursor-pointer transition-all duration-300 group"
                style={{ background: `${link.color}06`, border: `1px solid ${link.color}18` }}
                onMouseEnter={e => { (e.currentTarget as HTMLElement).style.borderColor = `${link.color}50`; (e.currentTarget as HTMLElement).style.background = `${link.color}12`; (e.currentTarget as HTMLElement).style.transform = 'translateY(-3px)'; }}
                onMouseLeave={e => { (e.currentTarget as HTMLElement).style.borderColor = `${link.color}18`; (e.currentTarget as HTMLElement).style.background = `${link.color}06`; (e.currentTarget as HTMLElement).style.transform = 'translateY(0)'; }}
              >
                <div className="w-8 h-8 flex items-center justify-center rounded-lg" style={{ background: `${link.color}15` }}>
                  <i className={`${link.icon} text-base`} style={{ color: link.color }} />
                </div>
                <span className="font-mono text-[10px] text-white/45 group-hover:text-white/70 transition-colors">{link.label}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom status line */}
        <div className="mt-12 flex items-center gap-3 font-mono text-[10px] text-white/15">
          <span>ERROR_CODE: 404</span>
          <span className="w-1 h-1 rounded-full bg-white/15" />
          <span>STATUS: NOT_FOUND</span>
          <span className="w-1 h-1 rounded-full bg-white/15" />
          <span style={{ color: 'rgba(255,45,120,0.4)' }}>NEONPHARMA_SYS</span>
        </div>
      </div>
    </div>
  );
}
